import React, { useState, useContext, useRef } from "react";
import {
    ChangePwdContent,
    ConfirmEditModal,
    ForgotPwdContent,
    LoginWithCode,
    LoginWithPassword,
    LogoIcon,
    TopMenu,
    useSafePromise,
    withMessage,
} from "remo-components";
import { WindowsSystemBtnContext } from "../components/StoreProvider";
import { RouteNameType, usePushHistory } from "../utils/routes";
import "./index.less";
import { getPreferencesStore, globalStore, useGlobalStore } from "@obsbot/remo-stores";
import { forgotPwd, loginCustom, sendVerifyCode } from "@obsbot/remo-server-api";
import { v4 as uuidv4 } from "uuid";
import { RemoI18n, useLanguage, useTranslate } from "@obsbot/remo-i18n";
import { Tabs } from "antd";
import { eventBus } from "@obsbot/remo-utils";

export enum PannelType {
    Login = "login",
    Register = "Register",
    ForgotPwd = "ForgotPwd",
}

const LoginPage: React.FC = () => {
    const privacyURL = "https://www.obsbot.cn/cn/service/docs/privacy-policy";
    const serviceURL = "https://www.obsbot.cn/cn/service/docs/terms-of-use";
    const t = useTranslate();
    const windowsBtnContext = useContext(WindowsSystemBtnContext);
    const lang = useLanguage();
    const sp = useSafePromise();
    const [pannelType, setPannelType] = useState<PannelType>(PannelType.Login);
    const pushHistory = usePushHistory();
    const { updateRTCInfo, updateServerRegionConfig, getUserInfo } = useGlobalStore();
    const [activeKey, setActiveKey] = useState(localStorage.getItem("loginType") || "code");
    // 提示用户目前注销状态
    const [alertCanceling, setAlertCanceling] = useState(false);
    const cacheLoginPayload = useRef<{ account: string; pwdOrCode: string } | null>(null);
    const [showForgot, setShowForgot] = useState<boolean>(false);
    // 账号修改验证的验证码是否出错
    const [isCodeError, setIsCodeError] = useState(false);
    const [curAccount, setCurAccount] = useState<string>("");
    // 忘记密码校验过的验证码
    const [forgetCode, setForgetCode] = useState("");
    // 密码修改的弹出框
    const [changePasswordVisible, setChangePasswordVisible] = useState(false);
    // 密码和原密码相同的错误提示
    const [isSamePassword, setIsSamePassword] = useState(false);

    // 点击login
    const login = async (account: string, pwdOrCode: string, force_flag = 0): Promise<boolean> => {
        try {
            const res = await loginCustom({
                account,
                password: activeKey === "pwd" ? pwdOrCode : "",
                login_type: activeKey,
                client_type: (window as any).isElectron ? "game_client" : "web",
                verification_code: activeKey === "code" ? pwdOrCode : "",
                mobile_id: uuidv4(),
                force_flag: force_flag,
            });
            localStorage.setItem("loginType", activeKey);
            localStorage.setItem("token", res.data.token);
            localStorage.setItem("refresh_token", res.data.refresh_token);
            updateServerRegionConfig({ tencent: { appId: res.data.sdkAppId } });
            updateRTCInfo({ userSig: res.data.userSig, uid: res.data.user_id });
            eventBus.emit("show-message", {
                type: "success",
                message: t("login-page.login-success"),
            });
            globalStore.updateOnlineStatus("online");
            await getUserInfo(res.data.image);
            if (useGlobalStore.getState().toJoinRoomUUID) {
                pushHistory(RouteNameType.ChannelRoomPage, {
                    roomUUID: useGlobalStore.getState().toJoinRoomUUID,
                });
            } else {
                // 判断首次登录,跳转到添加好友页面，非首次登录跳转到在线好友页面
                pushHistory(
                    res?.data?.image
                        ? RouteNameType.AddFriendsPage
                        : RouteNameType.OnlineFriendsPage,
                );
            }
            const preferenceStore = getPreferencesStore(res.data.user_id);
            RemoI18n.changeLanguage(preferenceStore?.language ?? "en");
            return !!res;
        } catch (err) {
            // NEW: 将服务端的密码格式错误直接抛出，供表单显示校验错误
            if (err?.code === "RM.100102") {
                throw err;
            }
            if (err.msg.includes("user is cancel status")) {
                cacheLoginPayload.current = { account, pwdOrCode };
                setAlertCanceling(true);
            }
            return false;
        }
    };
    // 点击注册
    const register = (): void => {
        setPannelType(PannelType.Register);
    };
    // 发送验证码
    const sendVerificationCode = async (account: string): Promise<boolean> => {
        const res = await sendVerifyCode({
            account,
            client_type: (window as any).isElectron ? "game_client" : "web",
            mobile_id: uuidv4(),
            msg_type: "safe_check",
            lan: "cn",
        });
        return res;
    };

    const toForgotPwd = (account: string): void => {
        setCurAccount(account);
        setShowForgot(true);
        setPannelType(PannelType.ForgotPwd);
    };

    // 通过验证码校验进行下一步
    const onNext = async (code: string): Promise<void> => {
        setForgetCode(code);
        setChangePasswordVisible(true);
        setShowForgot(false);
    };

    const onGetCode = async (): Promise<boolean> => {
        return sp(
            sendVerifyCode({
                account: curAccount,
                msg_type: "forget_pwd",
                client_type: (window as any).isElectron ? "game_client" : "web",
                mobile_id: uuidv4(),
                lan: lang === "zh-CN" ? "cn" : lang,
            }),
        )
            .then(res => {
                console.log("获取验证码", res);
                if (res.verification_code === null) {
                    setShowForgot(true);
                    return true;
                }
                return false;
            })
            .catch(() => {
                return false;
            });
    };

    // 修改新密码
    const handleChangePwd = async (code: string, newPass: string): Promise<void> => {
        const res = await forgotPwd({
            account: curAccount,
            verification_code: code,
            password: newPass,
            mobile_id: uuidv4(),
            client_type: (window as any).isElectron ? "game_client" : "web",
        });
        if (res.user_id) {
            eventBus.emit("show-message", {
                message: t("login-page.has-reset-password"),
                type: "success",
            });
            setForgetCode("");
            setChangePasswordVisible(false);
            setPannelType(PannelType.Login);
        }
    };

    const props = {
        login,
        register,
        sendVerificationCode,
        toForgotPwd,
        serviceURL,
        privacyURL,
    };
    const loginTypeItems = [
        {
            label: t("login-page.verify-code-login"),
            key: "code",
            children: <LoginWithCode {...props} />,
        },
        {
            label: t("login-page.pwd-login"),
            key: "pwd",
            children: <LoginWithPassword {...props} />,
        },
    ];

    return (
        <div className="LoginPageContainer">
            <TopMenu
                overlay={false}
                showLogo={false}
                onClickSystemBtn={windowsBtnContext?.onClickWindowsSystemBtn}
            />
            <div className="login-page-main-content">
                {pannelType === PannelType.ForgotPwd ? (
                    <div className="login-forgot-pwd">
                        {showForgot && (
                            <ForgotPwdContent
                                account={curAccount}
                                isCodeError={isCodeError}
                                setIsCodeError={setIsCodeError}
                                setVisible={() => {
                                    setShowForgot(false);
                                    setPannelType(PannelType.Login);
                                }}
                                visible={true}
                                onGetCode={onGetCode}
                                onNext={onNext}
                            />
                        )}
                        {changePasswordVisible && (
                            <ChangePwdContent
                                hideDesc={true}
                                isPassSameError={isSamePassword}
                                noSameVerify={true}
                                setIsPassSameError={setIsSamePassword}
                                setVisible={() => {
                                    setChangePasswordVisible(false);
                                    setPannelType(PannelType.Login);
                                }}
                                validCode={forgetCode}
                                visible={true}
                                onConfirm={handleChangePwd}
                            />
                        )}
                    </div>
                ) : (
                    <div className={"login-panel " + pannelType + " " + activeKey}>
                        <LogoIcon className="logo-icon" />
                        <Tabs
                            activeKey={activeKey}
                            items={loginTypeItems}
                            onChange={key => setActiveKey(key)}
                        ></Tabs>
                    </div>
                )}
                <ConfirmEditModal
                    content={t("deregisterAccount.canceling")}
                    setVisible={setAlertCanceling}
                    visible={alertCanceling}
                    onCancel={() => setAlertCanceling(false)}
                    onConfirm={async () => {
                        if (cacheLoginPayload.current) {
                            const { account, pwdOrCode } = cacheLoginPayload.current;
                            await login(account, pwdOrCode, 2);
                            setAlertCanceling(false);
                        }
                    }}
                />
            </div>
            <div className="lightShadowLeftTop"></div>
            <div className="lightShadowMiddleBottom"></div>
        </div>
    );
};

export default withMessage(LoginPage);
